import { Target, MonitorPlay, CheckCircle2 } from "lucide-react";

export default function ProcessSection() {
  const stages = [
    {
      icon: Target,
      title: "Hunter",
      tagline: "Contact prospects every day without adding headcount.",
      bullets: [
        "Calls prospects on its own, with no extra staff needed.",
        "Answers inbound calls right away and calls back in real time.",
        "Asks the right questions and reads buying intent as the call happens.",
      ],
    },
    {
      icon: MonitorPlay,
      title: "Nurture",
      tagline: "Explain the product clearly and shorten the sales cycle.",
      bullets: [
        "Presents product information in a clear structure, tuned to each industry and buyer.",
        "Walks prospects through features in a live AI demo and answers questions on the spot.",
        "Spots objections and responds right away.",
      ],
    },
    {
      icon: CheckCircle2,
      title: "Closure",
      tagline: "Your reps only spend time on sales-ready prospects.",
      bullets: [
        "Confirms budget, authority, urgency and scope.",
        "Prepares the demo so nothing is missed.",
        "Hands your rep the chat history, objections and intent.",
      ],
    },
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-24 bg-[#F8FAFC] border-b border-slate-200/80">
      <div className="w-full max-w-[1200px] mx-auto px-8 relative z-10">
        {/* Heading */}
        <div className="max-w-3xl mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-4">
            One AI agent from first call to handoff
          </h2>
          <p className="text-base sm:text-lg text-slate-600">
            EchoLeads handles the work in three stages, then passes a warm,
            well-briefed lead to your human rep.
          </p>
        </div>

        {/* 3 Connected Pipeline Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
          {stages.map((stage, idx) => {
            const IconComponent = stage.icon;
            return (
              <div key={idx} className="space-y-5">
                {/* Step Connector Line & Dot */}
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-[#F2A339]" />
                  <div className="h-[1.5px] flex-1 bg-slate-200" />
                </div>

                {/* Amber Icon Badge */}
                <div className="w-12 h-12 rounded-xl bg-[#FEF3C7] border border-[#FDE68A]/60 flex items-center justify-center text-[#D97706] mb-2">
                  <IconComponent className="w-5 h-5" />
                </div>

                {/* Title & Tagline */}
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {stage.title}
                  </h3>
                  <p className="text-base font-bold text-slate-900 leading-snug">
                    {stage.tagline}
                  </p>
                </div>

                {/* Bullet Points with ○ */}
                <ul className="space-y-3.5 pt-1">
                  {stage.bullets.map((bullet, bIdx) => (
                    <li key={bIdx} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                      <span className="text-[#F2A339] text-base leading-none select-none mt-0.5">○</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
